import { useCallback, useEffect } from "react";
import { NicheSubscriptionSource } from "@/common/buses";
import { useFollowNicheQueries } from "@/common/hooks/useFollowNicheQueries";
import { useNicheFollowsStore } from "@/common/store/nicheFollowsStore";
import { apiNiches } from "../services";
import { isAuthenticated } from "../services/Auth";

export const useNicheSubscription = (nicheId: string, origin: NicheSubscriptionSource) => {
  const subscribed = useNicheFollowsStore((state) => state.follows.includes(nicheId));
  const isFetched = useNicheFollowsStore((state) => state.isFetched);
  const follow = useNicheFollowsStore((state) => state.follow);
  const unfollow = useNicheFollowsStore((state) => state.unfollow);
  const replaceAllFollows = useNicheFollowsStore((state) => state.replaceAllFollows);
  const setFetched = useNicheFollowsStore((state) => state.setFetched);
  const setLoading = useNicheFollowsStore((state) => state.setLoading);

  useEffect(() => {
    const { isFetched: alreadyFetched, isLoading } = useNicheFollowsStore.getState();

    if (alreadyFetched || isLoading) {
      return;
    }

    (async () => {
      setLoading(true);

      try {
        if (await isAuthenticated()) {
          const { niches } = await apiNiches.getFollowingNiches();

          replaceAllFollows(niches.map((niche) => niche.id));
        } else {
          replaceAllFollows([]);
        }

        setFetched(true);
      } catch (error) {
        console.error("Could not load followed niches:", error);
      } finally {
        setLoading(false);
      }
    })();
  }, [replaceAllFollows, setFetched, setLoading]);

  // Called by `useFollowNicheQueries` only after the API call succeeded.
  const sendFollowState = useCallback((id: string, status: boolean) => {
    if (status) {
      follow(id);
    } else {
      unfollow(id);
    }
  }, [follow, unfollow]);

  const { onFollowAction } = useFollowNicheQueries({
    nicheId,
    following: subscribed,
    origin,
    sendFollowState
  });

  return {
    subscribed,
    isFetched,
    toggleSubscription: onFollowAction
  };
};
